import { useEffect } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_VOICEMAIL } from "@/constants/New";
import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputMedia from "@/design/forms/InputMedia";
import InputNumber from "@/design/forms/InputNumber";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viVoicemail } from "@sippy-platform/valkyrie";

import { Container, Grid, IconButton, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import voicemailReducer from "./voicemailReducer";

function Voicemail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const soundStore = useSelector((state) => state.domain.sounds);

  // Get general data
  useEffect(() => {
    Promise.resolve(Api.getSounds());
  }, []);

  const {
    value: voicemail,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_VOICEMAIL,
    reducer: voicemailReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 80], { message: "Name must be at most 80 characters." }),
      exten: number()
        .required({ message: "Number is required." })
        .outside([100, 119], { message: "Number cannot be between 100 and 119." })
        .not(0, { message: "Number cannot be 0." })
        .not(1711, { message: "Number cannot be 1711." })
        .not(1712, { message: "Number cannot be 1712." })
        .not(1722, { message: "Number cannot be 1722." })
        .not(1733, { message: "Number cannot be 1733." })
        .not(1813, { message: "Number cannot be 1813." }),
      email: string()
        .required({ message: "Email address is required." })
        .email({ message: "Email address is invalid." })
        .length([1, 120], { message: "Email address must be at most 120 characters." }),
    }),
    onSubmit: () => Api.storeVoicemail(voicemail),
    onSuccess: (response) => {
      if (!voicemail.id) {
        dispatch({ type: "id", value: response.data.voicemail.id });
        navigate(`../${response.data.voicemail.id}`);
      }

      Promise.resolve(Api.getVoicemails());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === voicemail.id || !id) {
      return;
    }

    setSaveState(SaveState.UNCHANGED);

    if (id === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_VOICEMAIL });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getVoicemail({ id: Number(id) }))
      .then((response) => !ignore && dispatch({ type: "set", value: response.data.voicemail }))
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [dispatch, id, setSaveState, voicemail.id]);

  // Handle changes
  const formHandler = (property, value) => {
    switch (property) {
      case "sound":
        dispatch({ type: property, value, list: soundStore.list });
        break;
      default:
        dispatch({ type: property, value });
        break;
    }

    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getVoicemails()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${voicemail.name || "Voicemail"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} /> <Avatar text={voicemail.name} icon={viVoicemail} />
              </IconButton>
            ) : (
              <Avatar text={voicemail.name} icon={viVoicemail} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={voicemail.id || "nk"}
                isActionable={!!voicemail?.id}
                onDeleteCheck={() => Api.deleteVoicemail(voicemail, false)}
                onDelete={() => Api.deleteVoicemail(voicemail, true)}
                name={`${voicemail.name} (${voicemail.exten})`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={voicemail.name || "Unnamed voicemail"}
          subheader={voicemail.exten || "No number set"}
        />
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="name" label="Name" onChange={formHandler} value={voicemail.name} error={errors} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumber
              required
              id="exten"
              label="Number"
              onChange={formHandler}
              value={voicemail.exten}
              error={errors}
              helperText="Number cannot be 0 or any Belgian emergency number."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText
              required
              id="email"
              label="Email"
              onChange={formHandler}
              value={voicemail.email}
              error={errors}
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Message">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              id="playGreeting"
              onChange={formHandler}
              states={[false, true]}
              value={voicemail.playGreeting}
              primary="Play greeting"
              secondary="Play a greeting before the voicemail."
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputMedia
              id="sound"
              label="Greeting"
              onChange={formHandler}
              value={voicemail.sound?.id}
              selectValue="id"
              selects={soundStore.list}
              getOptionLabel={(option) => option.name}
              error={errors}
              getSourceDependency={(option) => option?.token}
              getSourceGeneration={(option) =>
                `${urls.audio}${pbx.context}%2F${option.id}.${option.contentType}?alt=media&token=${option.token}`
              }
              hasEmpty={null}
              helperText="Leave empty to use the default."
            />
          </Grid>
        </FormGroup>
      </Stack>
    </Container>
  );
}

export default Voicemail;
